import { Capacitor, registerPlugin } from "@capacitor/core";
import {
  PublicClientApplication,
  PopupRequest,
  SilentRequest,
  AuthenticationResult,
  InteractionRequiredAuthError,
} from "@azure/msal-browser";
import { msalConfig } from "../authConfig";

// Declare the plugin interface since type declarations aren't published
interface MsAuthPluginType {
  login(options: {
    clientId: string;
    tenant: string;
    domainHint?: string;
    scopes: string[];
    keyHash?: string;
    authorityType?: string;
    authorityUrl?: string;
  }): Promise<{ accessToken: string }>;
  logout(options: {
    clientId: string;
    tenant: string;
    domainHint?: string;
    keyHash?: string;
  }): Promise<void>;
}

// Native plugin bridge (VelocityCubed MSAuthPlugin)
const MsAuthPlugin = registerPlugin<MsAuthPluginType>("MsAuthPlugin");

// Shared configuration values
const clientId = "9dd2f326-b763-439d-93e6-fbe5fd59ecc0";
const tenant = "5d8b875f-98bf-45d9-ada4-510e6872acac";
const domainHint = "maxigroup.co.uk";
const scopes = ["User.Read", "Sites.ReadWrite.All"];
// Android only: the base64‑encoded SHA‑1 key hash for your keystore
const keyHash = "IN5L3IEPwQHkRtHa0XVqrj/hUj0=";
let isInitialized = false;

// ------------- Web (MSAL.js) setup -------------

export const msalInstance = new PublicClientApplication(msalConfig);

// ------------- Function to ensure msal is initialised ---------------------

export async function ensureMsalInitialized() {
  if (!isInitialized) {
    await msalInstance.initialize();
    isInitialized = true;
  }
}

// ------------- Get native otions ---------------

function getNativeAuthOptions() {
  console.debug("[MSAL] Logging in via", Capacitor.getPlatform());
  const options: any = { clientId, tenant, scopes };
  if (domainHint) options.domainHint = domainHint;
  if (keyHash) options.keyHash = keyHash;
  return options;
}

// ------------- Build fake Auth Result ----------

function buildNativeAuthResult(token: string): AuthenticationResult {
  return {
    accessToken: token,
    account: {
      homeAccountId: "native_user",
      environment: "native",
      tenantId: tenant,
      username: "native@user",
      localAccountId: "native_local",
    },
    authority: `https://login.microsoftonline.com/${tenant}`,
    idToken: "",
    idTokenClaims: {},
    scopes,
    expiresOn: new Date(Date.now() + 3600 * 1000),
    tenantId: tenant,
    tokenType: "Bearer",
    uniqueId: "",
    fromCache: false,
    correlationId: "native-login",
  };
}

// ------------- Create Pop-up Request ------------

function getPopupRequest(): PopupRequest{
  return {
    scopes,
    prompt: "select_account",
    loginHint: domainHint
  };
}

// ------------- Unified Auth Service -------------

/**
 * Logs in the user on web or mobile.
 */
export async function login(): Promise<AuthenticationResult> {
  if (Capacitor.getPlatform() === "web") {
    await ensureMsalInitialized();

    let account = msalInstance.getActiveAccount();

    // If no active account, try getting the first available one
    if (!account) {
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        account = accounts[0];
        msalInstance.setActiveAccount(account);
      }
    }

    // Still no account? Interactive login
    if (!account) {
      console.warn("[MSAL] No account found, triggering loginPopup");
      const interactiveResult = await msalInstance.loginPopup(getPopupRequest());

      if (!interactiveResult.account) {
        throw new Error("Login succeeded but no account returned.");
      }

      msalInstance.setActiveAccount(interactiveResult.account);
      return interactiveResult;
    }

    // Proceed with silent token fetch
    const silentReq: SilentRequest = {
      account,
      scopes,
      forceRefresh: false,
    };

    return await msalInstance.acquireTokenSilent(silentReq);
  } else {
    // Native (Capacitor)
    const nativeResult = await MsAuthPlugin.login(getNativeAuthOptions());
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      msalInstance.setActiveAccount(accounts[0]);
    }

    return buildNativeAuthResult(nativeResult.accessToken);
  }
}


/**
 * Acquires an access token silently (or falls back to interactive on mobile).
 */
export async function acquireTokenSilent(): Promise<string> {
  if (Capacitor.getPlatform() === "web") {
    await ensureMsalInitialized();

    let account = msalInstance.getActiveAccount();

    if (!account) {
      console.warn(
        "[MSAL] No active account found. Attempting interactive login..."
      );

      const loginResult = await msalInstance.loginPopup(getPopupRequest());

      account = loginResult.account;
      console.debug("[MSAL] Logging in via", Capacitor.getPlatform());
      if (!account) throw new Error("Login succeeded but no account returned.");

      msalInstance.setActiveAccount(account);
    }

    const silentReq: SilentRequest = {
      account,
      scopes,
      forceRefresh: false,
    };

    const result = await msalInstance.acquireTokenSilent(silentReq);
    return result.accessToken;
  } else {
    const result = await MsAuthPlugin.login(getNativeAuthOptions());

    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      msalInstance.setActiveAccount(accounts[0]);
      console.debug("[MSAL] Logging in via", Capacitor.getPlatform());
    }

    return result.accessToken;
  }
}

/**
 * Logs out the user on web or mobile.
 */
export async function logout(): Promise<void> {
  if (Capacitor.getPlatform() === "web") {
    await ensureMsalInitialized();
    const account = msalInstance.getActiveAccount();
    if (account) {
      await msalInstance.logoutPopup({ account: account });
    }
  } else {
    await MsAuthPlugin.logout(getNativeAuthOptions());
  }
}
